import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui';
import { formatDate } from '@/lib/utils';
import type { AdminTraceTimelineEvent, TraceTimelineEventType } from '@/types/admin-trace';

// Typ-spezifische Labels/Farben — Stil-Anlehnung an die Phase-1-Timeline
const TYPE_CONFIG: Record<TraceTimelineEventType, { labelKey: string; className: string }> = {
  status_change: { labelKey: 'trace.timeline.types.statusChange', className: 'border-blue-300 bg-blue-50 text-blue-700' },
  iq_report: { labelKey: 'trace.timeline.types.iqReport', className: 'border-amber-300 bg-amber-50 text-amber-700' },
  notification_attempt: { labelKey: 'trace.timeline.types.notificationAttempt', className: 'border-green-300 bg-green-50 text-green-700' },
  subscription_event: { labelKey: 'trace.timeline.types.subscriptionEvent', className: 'border-violet-300 bg-violet-50 text-violet-700' },
  error: { labelKey: 'trace.timeline.types.error', className: 'border-red-300 bg-red-50 text-red-700' },
};

const FALLBACK_CONFIG = { labelKey: 'trace.timeline.types.unknown', className: 'border-gray-300 bg-gray-50 text-gray-700' };

/** Geparste Report-Felder (Spec 0178) — nur befuellte anzeigen. */
function ReportDetails({ event }: { event: AdminTraceTimelineEvent }) {
  const details = event.details;
  if (!details) return null;

  const entries = Object.entries(details).filter(([, v]) => v != null && v !== '');
  if (entries.length === 0) return null;

  return (
    <dl className="mt-1 space-y-0.5">
      {entries.map(([key, value]) => (
        <div key={key} className="flex gap-2 text-xs">
          <dt className="w-32 shrink-0 text-muted-foreground">{key}</dt>
          <dd className="break-all font-mono">{String(value)}</dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * DDB-Timeline des Trace (Spec 143 §5.3) — 5 Event-Typen.
 * Getrennt von den CloudWatch-Events (Trap §8.4: nicht mischen).
 */
export function TraceTimeline({ timeline }: { timeline: AdminTraceTimelineEvent[] }) {
  const { t } = useTranslation('transactions');

  if (timeline.length === 0) {
    return (
      <p className="py-4 text-center text-sm text-muted-foreground" data-testid="trace-timeline-empty">
        {t('trace.timeline.empty')}
      </p>
    );
  }

  return (
    <ul className="space-y-3" data-testid="trace-timeline">
      {timeline.map((event, idx) => {
        const config = TYPE_CONFIG[event.type] ?? FALLBACK_CONFIG;
        return (
          <li key={event.eventId ?? idx} className="border-l-2 border-border pl-3">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="outline" className={config.className}>
                {t(config.labelKey, event.type)}
              </Badge>
              {event.phase && (
                <span className="text-xs text-muted-foreground">{event.phase}</span>
              )}
              {event.timestamp && (
                <span className="text-xs text-muted-foreground">{formatDate(event.timestamp)}</span>
              )}
              {event.source && (
                <span className="text-xs text-muted-foreground">({event.source})</span>
              )}
            </div>
            {event.type === 'iq_report' && <ReportDetails event={event} />}
          </li>
        );
      })}
    </ul>
  );
}
